import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import camelCase from 'lodash/camelCase'
import startCase from 'lodash/startCase'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { AppStateContext } from '../../../providers/AppStateContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { Trail } from '../../../models/Trail.model'
import { TrailRelations } from '../../../models/TrailRelations.model'
import { Difficulties } from '../../../models/Difficulties.model'

import UserService from '../../../services/UserService/UserService'
import { TrailService } from '../../../services/TrailService/TrailService'
import { TrailWayService } from '../../../services/TrailWayService/TrailWayService'

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import LightboxImage from '../../../components/LightboxImage/LightboxImage.component'
import Tabs from '../../../components/Tabs/Tabs.component'
import AlgoVisualizer from '../../../components/AlgoVisualizer/AlgoVisualizer.component'
import ManualTrailStatusSearch from '../../ManualTrailStatuses/ManualTrailStatusSearch/ManualTrailStatusSearch.route'
import SponsorSearch from '../../Sponsors/SponsorSearch/SponsorSearch.route'
import TrailFeatureSearch from '../../TrailFeatures/TrailFeatureSearch/TrailFeatureSearch.route'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import TrailForm from '../shared/TrailForm.component'
import FeaturesOnTrailForm from '../shared/FeaturesOnTrailForm.component'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import RefreshIcon from '@mui/icons-material/Refresh'
import ObstructionReportsSearch from '../../ObstructionReports/ObstructionReportsSearch/ObstructionReportsSearch.route'
import { ConfigService } from '../../../services/ConfigService/ConfigService'
import { TrailMap } from '../../../components/TrailMap/TrailMap.component'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'
import { TrailSystemRelations } from '../../../models/TrailSystemRelations.model'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { TrailSectionIds, trailSections } from '../shared/TrailSections'

const TrailDetail = (): JSX.Element => {
  const trailFeatureSearchRef = useRef<{ openImportExport: () => void }>(null)
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrail])
  const hasWeatherGroupPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherGroup])
  const hasWeatherRecordPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherRecord])
  const hasManualTrailStatusPermissions = UserService.hasPermissions(user, [AppPermissions.editManualTrailStatus])
  const hasConditionalStatusPermissions = UserService.hasPermissions(user, [AppPermissions.viewConditionalStatus])
  const hasSponsorPermissions = UserService.hasPermissions(user, [AppPermissions.viewSponsor])
  const hasTrailFeaturePermissions = UserService.hasPermissions(user, [AppPermissions.viewTrailFeature])
  const hasObstructionReportPermissions = UserService.hasPermissions(user, [AppPermissions.viewObstructionReport])
  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isEditingFeaturesOnTrail, setIsEditingFeaturesOnTrail] = useState<boolean>(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const trailService = useMemo(() => new TrailService(), []) // memo to prevent re-creating service on every render
  const trailWayService = useMemo(() => new TrailWayService(), [])
  const [isRefreshingMap, setIsRefreshingMap] = useState(false)

  useEffect(() => {
    AppLayoutContext.setPageName(`Trail Detail`)
  }, [id])

  const queryClient = useQueryClient()
  // hook to get the precipitation lookback days for the region if not given for the trail system
  const { data: regionConfig } = useQuery(['ConfigService.get'], () => new ConfigService().get())

  const trailRelations = hasWeatherGroupPermissions
    ? [
        TrailRelations.system,
        TrailRelations.ways,
        TrailRelations.weatherGroups,
        TrailRelations.conditionalStatusGroups,
        TrailRelations.trailforksIds,
      ]
    : [TrailRelations.system, TrailRelations.ways, TrailRelations.trailforksIds]
  const { isLoading, data: trail } = useQuery(
    ['TrailService.get', { id: Number(id), relations: trailRelations }, AppStateContext.getRegion()],
    () => trailService.get({ id: Number(id), relations: trailRelations }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load trail.')
        console.error(err)
      },
    },
  )

  // parking lots live on the trail system; fetch them so a linked lot can be shown by name
  const trailSystemService = useMemo(() => new TrailSystemService(), [])
  const { data: systemWithParkingLots } = useQuery(
    [
      'TrailSystemService.get',
      { id: trail?.system?.id, relations: [TrailSystemRelations.parkingLots] },
      AppStateContext.getRegion(),
    ],
    () => trailSystemService.get({ id: trail?.system?.id, relations: [TrailSystemRelations.parkingLots] }),
    { enabled: !!trail?.system?.id && !!trail?.parkingLotId },
  )

  // precedence: trail > trail system > region > 7 days
  const precipitationLookback: { value: number | string; inheritedFrom?: string } =
    typeof trail?.precipitationLookbackDays === 'number'
      ? { value: trail.precipitationLookbackDays }
      : typeof trail?.system?.precipitationLookbackDays === 'number'
      ? { value: trail.system.precipitationLookbackDays, inheritedFrom: 'Trail System' }
      : regionConfig?.precipitationLookbackDays
      ? { value: regionConfig.precipitationLookbackDays, inheritedFrom: 'Region' }
      : { value: 7, inheritedFrom: 'Default' }

  const handleEdit = () => {
    closeEditor()
    setIsEditingFeaturesOnTrail(false)
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  // Force the API to re-pull this trail's geometry from OpenStreetMap (bypassing
  // the cache), then re-render the map so the admin can confirm an OSM edit took
  // hold without waiting for the cache to expire.
  const handleRefreshMap = async () => {
    if (!trail?.id || isRefreshingMap) return
    setIsRefreshingMap(true)
    try {
      await trailWayService.refreshTrailMap({ trailId: trail.id })
      await queryClient.invalidateQueries(['TrailWayService.getTrailMap', { trailId: trail.id }])
      SnackbarContext.show('Trail map refreshed from OpenStreetMap.')
    } catch (err) {
      console.error(err)
      SnackbarContext.show(`Failed to refresh trail map: ${err}`, 'error')
    } finally {
      setIsRefreshingMap(false)
    }
  }

  const handleDelete = () => {
    if (trail?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      trailService
        .delete(trail.id)
        .then(() => {
          SnackbarContext.show(`Trail deleted successfully. You will now be redirected.`, 'success', APP_UPDATE_NOTICE)

          // after a delay of 1 second, redirect to the trail system detail page (since normally someone comes via the trail system detail page)
          setTimeout(() => {
            navigate(`/trail-systems/${trail.system?.id}`)
            queryClient.removeQueries(['TrailService.get', { id: Number(id), relations: trailRelations }])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Trail failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const friendlyDifficulty = Object.entries(Difficulties).find(
    (eachDifficulty) => eachDifficulty[1] === trail?.difficultyId,
  )

  const tabs = useMemo(() => {
    const tabs = []
    if (trail) {
      if (hasWeatherRecordPermissions) {
        tabs.push({
          label: 'Algorithm',
          content: <AlgoVisualizer trail={trail} />,
        })
      }
      if (hasManualTrailStatusPermissions) {
        tabs.push({
          label: 'Manual Statuses',
          content: <ManualTrailStatusSearch nested trailId={trail.id} hideHeader excludeColumns={['system']} />,
        })
      }

      if (hasSponsorPermissions) {
        tabs.push({
          label: 'Sponsors',
          content: <SponsorSearch nested trailId={trail.id} hideHeader excludeColumns={['system']} />,
        })
      }
      if (hasObstructionReportPermissions) {
        tabs.push({
          label: 'Obstruction Reports',
          content: (
            <ObstructionReportsSearch nested trailId={trail.id} hideHeader excludeColumns={['system', 'trailName']} />
          ),
        })
      }
      if (hasTrailFeaturePermissions) {
        tabs.push({
          label: 'Features',
          content: (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" spacing={2} mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<ImportExportIcon />}
                  color="primary"
                  aria-label="show import export"
                  onClick={() => {
                    trailFeatureSearchRef.current?.openImportExport()
                  }}
                >
                  Export
                </Button>
                {hasWritePermissions && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setIsEditingFeaturesOnTrail(true)
                    }}
                  >
                    Edit Features On Trail
                  </Button>
                )}
              </Stack>
              <TrailFeatureSearch
                nested
                hideHeader
                hideActions
                disableImport
                disablePadding
                trailId={trail.id}
                ref={trailFeatureSearchRef}
              />
            </Paper>
          ),
        })
      }
    }
    return tabs
  }, [trail, hasWeatherRecordPermissions, hasManualTrailStatusPermissions, hasSponsorPermissions])

  const inAppUrl = trail?.system
    ? `https://${AppStateContext.getRegion().toLowerCase()}.flowfeed.app/trail-system/${trail.system.permalink}/${
        trail.permalink
      }`
    : ''
  const sortedWeatherGroups = [...(trail?.weatherGroups ?? [])].sort((a, b) => a.priority - b.priority)
  const linkedParkingLot = trail?.parkingLotId
    ? systemWithParkingLots?.parkingLots?.find((lot) => lot.id === trail.parkingLotId)
    : undefined
  const sectionById = (sectionId: TrailSectionIds) =>
    trailSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: TrailSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined
  const isUnset = (value: unknown) => value === null || value === undefined

  return (
    <>
      {trail && trail.system && (
        <FormDialog<Trail> open={isEditing} entity={trail} entityLabel="Trail" onClose={closeEditor}>
          <TrailForm
            trail={trail}
            trailSystem={trail.system}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      {trail && (
        <FormDialog<Trail>
          open={isEditingFeaturesOnTrail}
          entity={trail}
          entityLabel="Features On Trail"
          onClose={() => setIsEditingFeaturesOnTrail(false)}
        >
          <FeaturesOnTrailForm trail={trail} onEdit={handleEdit} onCancel={() => setIsEditingFeaturesOnTrail(false)} />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this trail?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && trail && !trail.system && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">Failed to locate parent trail system.</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && trail && trail.system && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={[
                      {
                        label: trail.system.name || 'Trail System',
                        href: `/trail-systems/${trail.system.id}`,
                      },
                      { label: trail.name },
                    ]}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={trail.name}
                  active={trail.active}
                  subtitle={
                    <>
                      <Link href={`/trail-systems/${trail.system.id}`}>{trail.system.name}</Link> · {trail.region} · ID{' '}
                      {trail.id}
                    </>
                  }
                  link={{ href: inAppUrl }}
                  meta={[
                    `Sort Weight: ${trail.sortWeight}`,
                    `Updated ${formatDate(trail.updatedAt)}`,
                    `Created ${formatDate(trail.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General + Trail Specs (left) ----------------------------- */}
                  <Grid item zero={12} md={5}>
                    <Stack spacing={2}>
                      <DetailSection
                        section={sectionById(TrailSectionIds.general)}
                        onEdit={editSectionHandler(TrailSectionIds.general)}
                        sx={{ height: 'auto' }}
                      >
                        {trail.featuredImgUrl && (
                          <LightboxImage
                            src={trail.featuredImgUrl}
                            alt={trail.name}
                            sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1 }}
                          />
                        )}
                        <DetailField label="Description">
                          <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>{trail.description || '-'}</Box>
                        </DetailField>
                      </DetailSection>
                      <DetailSection
                        section={sectionById(TrailSectionIds.specs)}
                        onEdit={editSectionHandler(TrailSectionIds.specs)}
                        sx={{ height: 'auto' }}
                      >
                        <DetailFieldGrid minColumnWidth={120}>
                          <DetailField label="Difficulty">
                            {friendlyDifficulty ? startCase(camelCase(friendlyDifficulty[0])) : '-'}
                          </DetailField>
                          <DetailField label="Surface">{trail.surfaceType || '-'}</DetailField>
                          <DetailField label="Distance">{trail.distance || 0} mi</DetailField>
                          <DetailField label="Climb">{trail.climb || 0} ft</DetailField>
                          <DetailField label="Descent">{trail.descent || 0} ft</DetailField>
                        </DetailFieldGrid>
                      </DetailSection>
                    </Stack>
                  </Grid>

                  {/* --- Map Data (right) ----------------------------------------- */}
                  <Grid item zero={12} md={7}>
                    <DetailSection
                      section={sectionById(TrailSectionIds.map)}
                      onEdit={editSectionHandler(TrailSectionIds.map)}
                      action={
                        <>
                          <Button
                            variant="text"
                            size="small"
                            href={`/trail-map/${trail.id}`}
                            sx={{ fontSize: '0.875rem' }}
                          >
                            Full Map
                          </Button>
                          {hasWritePermissions && (
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={handleRefreshMap}
                              disabled={isRefreshingMap}
                              title="Re-pull this trail's geometry from OpenStreetMap"
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {isRefreshingMap ? 'Refreshing…' : 'Refresh'}
                            </Button>
                          )}
                        </>
                      }
                    >
                      <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
                        <TrailMap trailId={trail.id} />
                      </Box>
                      <DetailField label="OpenStreetMap IDs">
                        {(trail.osmRelations ?? []).length === 0 && trail.ways.length === 0 ? (
                          <Typography variant="body1" color="error">
                            No relation or way IDs. The trail can't be drawn on the map.
                          </Typography>
                        ) : (
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {(trail.osmRelations ?? []).map((relation) => (
                              <Chip
                                key={`relation-${relation.relationId}`}
                                size="small"
                                variant="outlined"
                                clickable
                                component="a"
                                href={`https://www.openstreetmap.org/relation/${relation.relationId}`}
                                target="_blank"
                                label={`Relation ${relation.relationId}`}
                                icon={<OpenInNewIcon />}
                              />
                            ))}
                            {trail.ways.map((way) => (
                              <Chip
                                key={`way-${way.wayId}`}
                                size="small"
                                variant="outlined"
                                clickable
                                component="a"
                                href={`https://www.openstreetmap.org/way/${way.wayId}`}
                                target="_blank"
                                label={`Way ${way.wayId}`}
                                icon={<OpenInNewIcon />}
                              />
                            ))}
                          </Stack>
                        )}
                      </DetailField>
                    </DetailSection>
                  </Grid>

                  {/* --- Location ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSectionIds.location)}
                      onEdit={editSectionHandler(TrailSectionIds.location)}
                    >
                      <DetailField label="Address">
                        <Box>
                          {trail.street}
                          <br />
                          {trail.city}, {trail.state}
                        </Box>
                      </DetailField>
                      <DetailFieldGrid>
                        <DetailField label="Trailhead GPS">
                          {!isUnset(trail.gpsLat) && !isUnset(trail.gpsLong) ? (
                            <Link
                              component="a"
                              href={`https://www.google.com/maps/search/?api=1&query=${trail.gpsLat},${trail.gpsLong}`}
                              target="_blank"
                            >
                              {trail.gpsLat}, {trail.gpsLong}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                        <DetailField label="Parking">
                          {!isUnset(trail.parkingGpsLat) && !isUnset(trail.parkingGpsLong) ? (
                            <Box>
                              {linkedParkingLot && (
                                <Typography variant="body1">{linkedParkingLot.name || 'Trail System Lot'}</Typography>
                              )}
                              <Link
                                component="a"
                                href={`https://www.google.com/maps/search/?api=1&query=${trail.parkingGpsLat},${trail.parkingGpsLong}`}
                                target="_blank"
                              >
                                {trail.parkingGpsLat}, {trail.parkingGpsLong}
                              </Link>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Weather & Algorithm -------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSectionIds.weather)}
                      onEdit={editSectionHandler(TrailSectionIds.weather)}
                    >
                      {hasWeatherGroupPermissions && (
                        <>
                          <DetailFieldGrid>
                            <DetailField
                              label="Algorithm Weather Groups"
                              inheritedFrom={sortedWeatherGroups.length ? undefined : 'Trail System'}
                            >
                              {sortedWeatherGroups.length > 0 && (
                                <Box component="ol" sx={{ m: 0, pl: 3 }}>
                                  {sortedWeatherGroups.map((wg) => (
                                    <li key={wg.id}>
                                      <Link href={`/weather-groups/${wg.id}`}>{wg.name}</Link>
                                    </li>
                                  ))}
                                </Box>
                              )}
                            </DetailField>
                            <DetailField
                              label="Display Weather Group"
                              inheritedFrom={trail.displayWeatherGroup?.id ? undefined : 'Trail System'}
                            >
                              {trail.displayWeatherGroup?.id && (
                                <Link href={`/weather-groups/${trail.displayWeatherGroup.id}`}>
                                  {trail.displayWeatherGroup.name}
                                </Link>
                              )}
                            </DetailField>
                          </DetailFieldGrid>
                          <Divider />
                        </>
                      )}
                      <DetailFieldGrid>
                        <DetailField label="Weather Resistance">{trail.weatherResistance}</DetailField>
                        <DetailField label="Precipitation Lookback" inheritedFrom={precipitationLookback.inheritedFrom}>
                          {precipitationLookback.value} days
                        </DetailField>
                        <DetailField
                          label="Saturation Modifier"
                          inheritedFrom={
                            !isUnset(trail.saturationModifier)
                              ? undefined
                              : !isUnset(trail.system.saturationModifier)
                              ? 'Trail System'
                              : 'Default'
                          }
                        >
                          {trail.saturationModifier ?? trail.system.saturationModifier ?? 1}
                        </DetailField>
                        <DetailField
                          label="Dry-Time Modifier"
                          inheritedFrom={
                            !isUnset(trail.dryTimeModifier)
                              ? undefined
                              : !isUnset(trail.system.dryTimeModifier)
                              ? 'Trail System'
                              : 'Default'
                          }
                        >
                          {trail.dryTimeModifier ?? trail.system.dryTimeModifier ?? 1}
                        </DetailField>
                        <DetailField
                          label="Freeze/Thaw"
                          inheritedFrom={
                            !isUnset(trail.freezeThawEnabled)
                              ? undefined
                              : !isUnset(trail.system.freezeThawEnabled)
                              ? 'Trail System'
                              : 'Region'
                          }
                        >
                          {(
                            !isUnset(trail.freezeThawEnabled)
                              ? trail.freezeThawEnabled
                              : !isUnset(trail.system.freezeThawEnabled)
                              ? trail.system.freezeThawEnabled
                              : regionConfig?.ftEnabled === 'true'
                          )
                            ? 'Enabled'
                            : 'Disabled'}
                        </DetailField>
                      </DetailFieldGrid>
                      {hasConditionalStatusPermissions && (
                        <>
                          <Divider />
                          <DetailField label="Conditional Status Groups">
                            {trail.conditionalStatusGroups && trail.conditionalStatusGroups.length > 0 ? (
                              <Stack direction="row" flexWrap="wrap" gap={1}>
                                {trail.conditionalStatusGroups.map((conditionalStatusGroup) => (
                                  <Chip
                                    key={conditionalStatusGroup.id}
                                    size="small"
                                    variant="outlined"
                                    clickable
                                    component={Link}
                                    href={`/conditional-status-groups/${conditionalStatusGroup.id}`}
                                    label={conditionalStatusGroup.name}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              '-'
                            )}
                          </DetailField>
                        </>
                      )}
                    </DetailSection>
                  </Grid>

                  {/* --- Integrations --------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSectionIds.integrations)}
                      onEdit={editSectionHandler(TrailSectionIds.integrations)}
                    >
                      <DetailFieldGrid>
                        <DetailField
                          label="Trailforks Status Sync"
                          inheritedFrom={isUnset(trail.trailforksSyncEnabled) ? 'Trail System' : undefined}
                        >
                          {trail.trailforksSyncEnabled ?? trail.system.trailforksSyncEnabled ? 'Enabled' : 'Disabled'}
                        </DetailField>
                        <DetailField label="Trailforks Trail IDs">
                          {(trail.trailforksIds ?? []).length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {(trail.trailforksIds ?? []).map((link) => (
                                <Chip
                                  key={link.trailforksTrailId}
                                  size="small"
                                  variant="outlined"
                                  label={link.trailforksTrailId}
                                />
                              ))}
                            </Stack>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                      </DetailFieldGrid>
                      {(trail.trailforksSyncEnabled ?? trail.system.trailforksSyncEnabled) &&
                        (trail.trailforksIds ?? []).length === 0 && (
                          <Typography variant="body2" color="warning.main">
                            Sync is enabled but no Trailforks Trail IDs are mapped, so this trail won't be pushed.
                          </Typography>
                        )}
                    </DetailSection>
                  </Grid>
                </Grid>

                {tabs.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Tabs tabs={tabs} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailDetail
